import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getProductos } from "../../../services/admin/productos";
import ProductViewModal from "./ProductViewModal";
import { IconView, IconEdit } from "../../../icons/Icons";
import InventoryLoadModal from "./InventoryLoadModal";
import InventoryAdjustModal from "./InventoryAdjustModal";
import { IconPlus, IconSettings } from "../../../icons/Icons";
import InventoryHistoryModal from "./InventoryHistoryModal";
import BarcodeLabelsModal from "./BarcodeLabelsModal";
import { IconHistory } from "../../../icons/Icons";
import { hasPerm } from "../../../utils/hasPerm";
import MultiSelect from "../../../components/admin/MultiSelect";
import {
  getCondiciones,
  getCertificados,
} from "../../../services/admin/atributos_producto";

function ProductsPage() {
  const navigate = useNavigate();

  const [productos, setProductos] = useState([]);
  // > 0 cuando el servidor devolvio solo una parte del listado.
  const [listaRecortada, setListaRecortada] = useState(0);

  // Escaneo con pistola: el lector se comporta como un teclado, teclea el
  // codigo en el campo y manda Enter.

  // Seleccion para imprimir etiquetas. Set de producto_id.
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [showEtiquetas, setShowEtiquetas] = useState(false);
  const [productosEtiquetas, setProductosEtiquetas] = useState([]);
  const [search, setSearch] = useState("");
  
  // Arrays y no un id: los filtros aceptan varios valores a la vez. Vacio
  // significa "sin filtrar", que no es lo mismo que "ninguno".
  const [categoriaIds, setCategoriaIds] = useState([]);
  const [marcaIds, setMarcaIds] = useState([]);
  const [condicionIds, setCondicionIds] = useState([]);
  const [certificadoIds, setCertificadoIds] = useState([]);

  // Los catalogos de condicion y certificado se piden al backend en vez de
  // deducirlos de los productos cargados: hoy ningun producto los tiene
  // asignados todavia, y deducirlos daria dos filtros vacios.
  const [catCondiciones, setCatCondiciones] = useState([]);
  const [catCertificados, setCatCertificados] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);

  const [showView, setShowView] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showLoadInventory, setShowLoadInventory] = useState(false);
  const [showAdjustInventory, setShowAdjustInventory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const alternarSeleccion = (id) => {
    setSeleccionados((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      return siguiente;
    });
  };

  const cargarProductos = useCallback(async () => {
    try {
      const { items, total, hasMore } = await getProductos();
      setProductos(items);
      setListaRecortada(hasMore ? total : 0);
    } catch (error) {
      console.error("Error cargando productos:", error);
      setProductos([]);
      setListaRecortada(0);
    }
  }, []);

  useEffect(() => {
    cargarProductos();
  }, [cargarProductos]);

  useEffect(() => {
    let vivo = true;

    (async () => {
      try {
        const [conds, certs] = await Promise.all([
          getCondiciones(),
          getCertificados(),
        ]);
        if (!vivo) return;
        setCatCondiciones(conds);
        setCatCertificados(certs);
      } catch (err) {
        // Si fallan, los otros filtros tienen que seguir funcionando.
        console.error("Error cargando condiciones/certificados:", err);
      }
    })();

    return () => {
      vivo = false;
    };
  }, []);

  const categorias = useMemo(() => {
    const base = marcaIds.length
      ? productos.filter((p) =>
          marcaIds.map(String).includes(String(p.marca_producto_id))
        )
      : productos;

    const map = new Map();
    base.forEach((p) => {
      const id = p.categoria_producto_id;
      if (id != null) {
        map.set(String(id), p.categoria_nombre || `#${id}`);
      }
    });

    return [...map.entries()]
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos, marcaIds]);

  const marcas = useMemo(() => {
    const base = categoriaIds.length
      ? productos.filter((p) =>
          categoriaIds.map(String).includes(String(p.categoria_producto_id))
        )
      : productos;

    const map = new Map();
    base.forEach((p) => {
      const id = p.marca_producto_id;
      if (id != null) {
        map.set(String(id), p.marca_nombre || `#${id}`);
      }
    });

    return [...map.entries()]
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [productos, categoriaIds]);

  // Al cruzarse los filtros, una opcion elegida puede desaparecer de la
  // lista disponible. Se saca de la seleccion en vez de dejarla filtrando en
  // silencio por algo que ya no se ve.
  useEffect(() => {
    setCategoriaIds((prev) => {
      const validos = prev.filter((id) =>
        categorias.some((c) => c.id === String(id))
      );
      return validos.length === prev.length ? prev : validos;
    });
  }, [categorias]);

  useEffect(() => {
    setMarcaIds((prev) => {
      const validos = prev.filter((id) => marcas.some((m) => m.id === String(id)));
      return validos.length === prev.length ? prev : validos;
    });
  }, [marcas]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return productos.filter((p) => {
      // El serial y la ubicacion tambien entran en la busqueda: son dos de
      // las formas naturales de buscar una pieza ("cual estaba en A-3").
      const text = `${p.nombre || ""} ${p.descripcion || ""} ${
        p.no_parte || ""
      } ${p.numero_serie || ""} ${p.ubicacion || ""}`.toLowerCase();

      const matchesSearch = !q || text.includes(q);

      // Sin seleccion, el filtro no filtra. Con varios, alcanza con que el
      // producto coincida con uno: son alternativas, no condiciones que se
      // sumen. Pedir que cumpla todas no devolveria nunca nada.
      const coincide = (ids, valor) =>
        ids.length === 0 || ids.map(String).includes(String(valor ?? ""));

      return (
        matchesSearch &&
        coincide(categoriaIds, p.categoria_producto_id) &&
        coincide(marcaIds, p.marca_producto_id) &&
        coincide(condicionIds, p.condicion_producto_id) &&
        coincide(certificadoIds, p.certificado_producto_id)
      );
    });
  }, [
    productos,
    search,
    categoriaIds,
    marcaIds,
    condicionIds,
    certificadoIds,
  ]);

  useEffect(() => {
    setTotalPages(Math.max(1, Math.ceil(filtered.length / pageSize)));
    setPage(1);
  }, [filtered.length, pageSize]);

  const start = (page - 1) * pageSize;
  const paginated = filtered.slice(start, start + pageSize);

  const nextPage = () => page < totalPages && setPage(page + 1);
  const prevPage = () => page > 1 && setPage(page - 1);

  const irACrear = () => navigate("/administrator_jx677/products/new");

  const irAEditar = (product) =>
    navigate(`/administrator_jx677/products/${product.producto_id}/edit`);

  const openViewModal = (product) => {
    setSelectedProduct(product);
    setShowView(true);
  };

  const openLoadInventory = (product) => {
    setSelectedProduct(product);
    setShowLoadInventory(true);
  };

  const openAdjustInventory = (product) => {
    setSelectedProduct(product);
    setShowAdjustInventory(true);
  };

  return (
    <div className="page-container">
      <style>{modernStyles}</style>

      {listaRecortada > 0 && (
        <div className="lista-recortada">
          Showing the first {productos.length} of {listaRecortada} records. Refine your
          filters or raise the page size to see the rest.
        </div>
      )}


      <div className="header">
        <h2 className="title">Products Management</h2>

        <div className="top-bar">
          {/* Dos zonas: arriba lo de buscar y crear, abajo los filtros.
              El campo de escaneo se mudo al sidebar: con la pistola en la
              mano no se quiere navegar hasta esta pantalla primero. Se abre
              desde cualquier lugar con Ctrl+K. */}
          <div className="barra-buscar">
            <input
              type="text"
              placeholder="Search product..."
              className="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

          </div>

          <div className="barra-filtros">
            <span className="barra-filtros__et">Filter</span>

            <MultiSelect
              valores={categoriaIds}
              onChange={setCategoriaIds}
              options={categorias.map((c) => ({ value: c.id, label: c.nombre }))}
              textoVacio="All categories"
              nombre="categories"
              buscarPlaceholder="Search category..."
            />

            <MultiSelect
              valores={marcaIds}
              onChange={setMarcaIds}
              options={marcas.map((m) => ({ value: m.id, label: m.nombre }))}
              textoVacio={marcas.length === 0 ? "No brands" : "All brands"}
              nombre="brands"
              buscarPlaceholder="Search brand..."
              disabled={marcas.length === 0}
            />

            {/* Estos dos vienen del catalogo, no de los productos cargados.
                La opcion "Not set" es la mas util ahora mismo: sirve para
                encontrar las piezas a las que todavia les falta el dato. */}
            <MultiSelect
              valores={condicionIds}
              onChange={setCondicionIds}
              options={[
                { value: "", label: "— Not set —" },
                ...catCondiciones.map((c) => ({ value: c.id, label: c.nombre })),
              ]}
              textoVacio="All conditions"
              nombre="conditions"
              buscarPlaceholder="Search condition..."
            />

            <MultiSelect
              valores={certificadoIds}
              onChange={setCertificadoIds}
              options={[
                { value: "", label: "— Not set —" },
                ...catCertificados.map((c) => ({ value: c.id, label: c.nombre })),
              ]}
              textoVacio="All certificates"
              nombre="certificates"
              buscarPlaceholder="Search certificate..."
            />

          </div>

          <div className="top-bar-right">
            {seleccionados.size > 0 && (
              <button
                className="btn-etiquetas"
                onClick={() => {
                  setProductosEtiquetas(
                    productos.filter((p) => seleccionados.has(p.producto_id))
                  );
                  setShowEtiquetas(true);
                }}
                title="Print barcode labels for the selected products"
              >
                Print labels ({seleccionados.size})
              </button>
            )}

            <button className="btn-create" onClick={irACrear}>
              Create Product
            </button>
          </div>
        </div>
      </div>

      <div className="table-card">
        <table className="modern-table">
          <thead>
            <tr>
              <th className="th-check">
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={
                    paginated.length > 0 &&
                    paginated.every((p) => seleccionados.has(p.producto_id))
                  }
                  onChange={(e) => {
                    const marcar = e.target.checked;
                    setSeleccionados((prev) => {
                      const siguiente = new Set(prev);
                      paginated.forEach((p) =>
                        marcar
                          ? siguiente.add(p.producto_id)
                          : siguiente.delete(p.producto_id)
                      );
                      return siguiente;
                    });
                  }}
                />
              </th>
              <th className="th-id">ID</th>
              <th>Product</th>
              <th>Category</th>
              <th>Brand</th>
              <th className="th-num">Available</th>
              <th className="th-num">Sold</th>
              <th className="th-num">Price</th>
              <th className="th-estado">Status</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-data">
                  No products found
                </td>
              </tr>
            ) : (
              paginated.map((p) => (
                <tr key={p.producto_id}>
                  <td className="td-check">
                    <input
                      type="checkbox"
                      aria-label={`Select ${p.nombre || p.producto_id}`}
                      checked={seleccionados.has(p.producto_id)}
                      onChange={() => alternarSeleccion(p.producto_id)}
                    />
                  </td>
                  <td className="td-id">{p.producto_id}</td>

                  {/* Nombre y numero de parte en una sola celda: el numero
                      de parte es justo el dato por el que se busca una pieza y
                      antes no estaba en ningun lado. */}
                  <td className="td-producto">
                    <span className="prod-nombre">{p.nombre}</span>
                    <span className="prod-meta">
                      <Dato valor={p.no_parte} etiqueta="Part number" />

                      <Dato valor={p.condicion_nombre} etiqueta="Condition" />
                      <Dato valor={p.certificado_nombre} etiqueta="Certificate" />
                    </span>
                  </td>

                  <td>{p.categoria_nombre || "-"}</td>
                  <td>{p.marca_nombre || "-"}</td>

                  <td className="td-num">
                    <strong
                      style={{
                        color:
                          p.cantidad_disponible === 0
                            ? "#b91c1c"
                            : p.cantidad_disponible < 5
                            ? "#d97706"
                            : "#15803d",
                      }}
                    >
                      {p.cantidad_disponible}
                    </strong>
                  </td>

                  <td className="td-num">
                    <span style={{ color: "#64748b" }}>
                      {p.cantidad_vendida}
                    </span>
                  </td>

                  {/* Con separador de miles: "$22500.00" y "$2250.00" se
                      confunden de un vistazo, y equivocarse por diez veces
                      en un precio no es un detalle. */}
                  <td className="td-num">{formatoPrecio(p.precio_unitario)}</td>

                  <td className="td-estado">
                    <span className={`status ${estadoVisible(p).clase}`}>
                      {estadoVisible(p).texto}
                    </span>
                  </td>

                  <td>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "flex-end",
                      }}
                    >
                      {hasPerm("View product") && <span
                        className="action-icon view"
                        onClick={() => openViewModal(p)}
                        title="View product"
                      >
                        {IconView}
                      </span>
                      }

                      {hasPerm("Update product") && <span
                        className="action-icon edit"
                        onClick={() => irAEditar(p)}
                        title="Edit product"
                      >
                        {IconEdit}
                      </span>
                      }

                      {hasPerm("Add inventory product") && <span
                        className="action-icon"
                        onClick={() => openLoadInventory(p)}
                        title="Add inventory"
                        style={{ background: "#ECFEFF" }}
                      >
                        {IconPlus}
                      </span>
                      }

                       {hasPerm("Adjust inventory product") &&<span
                        className="action-icon"
                        onClick={() => openAdjustInventory(p)}
                        title="Adjust inventory"
                        style={{ background: "#FFF7ED" }}
                      >
                        {IconSettings}
                      </span>
                      }

                      {hasPerm("Inventory history product") &&<span
                        className="action-icon"
                        onClick={() => {
                          setSelectedProduct(p);
                          setShowHistory(true);
                        }}
                        title="Inventory history"
                        style={{ background: "#EEF2FF" }}
                      >
                        {IconHistory}
                      </span>
                      }
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* El tamano de pagina vive aca abajo, junto a Prev/Next: es parte de
          como se recorre la lista, no de como se filtra. Arriba, entre los
          filtros, parecia uno mas de ellos. */}
      <div className="pagination">
        {/* Hueco a la izquierda del mismo ancho que el total de la derecha,
            para que el grupo del centro quede centrado de verdad. */}
        <span className="pag-lado" aria-hidden="true" />

        <div className="pag-nav">
          <button onClick={prevPage} disabled={page === 1}>
            Prev
          </button>

          <span>
            Page {page} of {totalPages}
          </span>

          <button onClick={nextPage} disabled={page === totalPages}>
            Next
          </button>

          {/* Pegado al paginador y no en la esquina: elegir cuantas filas se
              ven es parte de moverse por las paginas. */}
          <div className="pag-tamano">
            <label htmlFor="pag-tamano">Rows</label>
            <select
              id="pag-tamano"
              className="page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Cuantas filas hay de verdad detras de los filtros. Con el tope de
            pagina puesto, "20 filas" no dice si hay 20 o 300. */}
        <span className="pag-total">
          {filtered.length}
          {filtered.length === 1 ? " product" : " products"}
        </span>
      </div>

      {showView && (
        <ProductViewModal
          isOpen={showView}
          onClose={() => setShowView(false)}
          product={selectedProduct}
        />
      )}

      {showLoadInventory && (
        <InventoryLoadModal
          isOpen={showLoadInventory}
          product={selectedProduct}
          onClose={() => setShowLoadInventory(false)}
          onSuccess={cargarProductos}
        />
      )}

      {showAdjustInventory && (
        <InventoryAdjustModal
          isOpen={showAdjustInventory}
          product={selectedProduct}
          onClose={() => setShowAdjustInventory(false)}
          onSuccess={cargarProductos}
        />
      )}

      {showEtiquetas && (
        <BarcodeLabelsModal
          isOpen={showEtiquetas}
          productos={productosEtiquetas}
          onClose={() => setShowEtiquetas(false)}
        />
      )}

      {showHistory && (
        <InventoryHistoryModal
          isOpen={showHistory}
          product={selectedProduct}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

export default ProductsPage;

/**
 * Precio con separador de miles.
 *
 * Sin el, "$22500.00" y "$2250.00" se confunden de un vistazo en una lista
 * larga, y equivocarse por diez veces en un precio no es un detalle.
 */
/**
 * El estado que se muestra en la lista, en una sola pastilla.
 *
 * Se puede resumir en una porque "inactivo y publicado" es un estado
 * imposible: al desactivar un producto se le quita la publicacion. Quedan
 * tres combinaciones reales, y estas tres las cubren sin perder nada:
 *
 *   Inactive       no sirve dentro del sistema
 *   Not published  activo, pero el cliente todavia no lo ve
 *   Published      activo y visible en la tienda
 *
 * El orden de las preguntas importa: inactivo gana, porque es lo primero
 * que hay que resolver antes de hablar de la tienda.
 */
const estadoVisible = (p) => {
  if (Number(p.estado) !== 1) {
    return { texto: "Inactive", clase: "inactive" };
  }
  if (Number(p.publicado) === 1) {
    return { texto: "Published", clase: "active" };
  }
  return { texto: "Not published", clase: "neutral" };
};

/**
 * Un dato de la pieza: el valor en una pastilla y debajo que cosa es.
 *
 * La etiqueta va abajo y no adelante porque adelante compite con el valor
 * por el ancho, y en una lista uno recorre los valores, no los nombres.
 * Devuelve null cuando no hay valor: una pastilla vacia con su etiqueta
 * ocuparia lugar para decir que no hay nada.
 */
const Dato = ({ valor, etiqueta }) => {
  if (!valor) return null;

  return (
    <span className="prod-dato">
      <span className="prod-dato-valor" title={etiqueta}>
        {valor}
      </span>
      <span className="prod-dato-et">{etiqueta}</span>
    </span>
  );
};

const formatoPrecio = (valor) => {
  const n = Number(valor);
  if (!Number.isFinite(n)) return "-";

  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const modernStyles = `
.page-container {
  /* Llena el alto de la tarjeta del layout. La pagina nunca scrollea:
     solo el listado, mas abajo. Antes usaba calc(100vh - 80px), una
     estimacion que no coincidia con el alto real y sobraba contenido. */
  height: 100%;
  padding: 4px 4px 0;
  font-family: 'Segoe UI';
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* Cabecera, aviso y paginacion mantienen su alto; no se comprimen. */
.page-container > .header,
.page-container > .pagination,
.page-container > .lista-recortada {
  flex: none;
}

.top-bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.btn-etiquetas {
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid #2F4779;
  background: #ffffff;
  color: #2F4779;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.btn-etiquetas:hover {
  background: #EFF6FF;
}

.th-check,
.td-check {
  width: 38px;
  text-align: center;
  padding-right: 0 !important;
}

.th-check input,
.td-check input {
  cursor: pointer;
  width: 15px;
  height: 15px;
}

/* Campo de escaneo: se distingue del buscador normal para que el operario
   sepa donde apuntar la pistola. */











/* TOP BAR */
.filter-select {
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  font-size: 14px;
  cursor: pointer;
  min-width: 180px;
}

.filter-select:focus {
  outline: none;
  border-color: #3B82F6;
}

.filter-select {
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  font-size: 14px;
  cursor: pointer;
  min-width: 180px;
}

/* opcional: mismo focus que search */
.filter-select:focus {
  outline: none;
  border-color: #3B82F6;
}

.page-container::-webkit-scrollbar,
.table-card::-webkit-scrollbar {
  width: 10px;
}

.page-container::-webkit-scrollbar-track,
.table-card::-webkit-scrollbar-track {
  background: #F1F5F9;
  border-radius: 10px;
}

.page-container::-webkit-scrollbar-thumb,
.table-card::-webkit-scrollbar-thumb {
  background: linear-gradient(
    180deg,
    #93C5FD,
    #3B82F6
  );
  border-radius: 10px;
  border: 2px solid #F1F5F9;
}

.page-container::-webkit-scrollbar-thumb:hover,
.table-card::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(
    180deg,
    #60A5FA,
    #2563EB
  );
}

/* ===== SCROLLBAR FIREFOX ===== */
.page-container,
.table-card {
  scrollbar-width: thin;
  scrollbar-color: #3B82F6 #F1F5F9;
}

/* HEADER */
.header {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title {
  font-size: 26px;
  /* 800 como el de Sales Orders: con 700 se veia mas flaco que el de al
     lado siendo el mismo tipo de titulo. */
  font-weight: 800;
  color: #2F4779;
}

/* TOP BAR */
/* Dos filas: arriba buscar y crear, abajo filtrar.
   Se logra con wrap y order, no con dos contenedores anidados: los filtros
   piden todo el ancho (flex-basis 100%) asi que caen solos a la fila de
   abajo, y el boton de crear se queda arriba a la derecha. */
.top-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.barra-buscar {
  order: 1;
  /* No se estira: el ancho sobrante es para los filtros, que son cuatro. */
  flex: 0 1 260px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.top-bar-right {
  order: 3;
  margin-left: auto;
  flex: none;
}

/* Todo en un renglon: buscar, los filtros y el boton. Antes los filtros
   pedian el 100% del ancho y se llevaban una fila entera para ellos. */
.barra-filtros {
  order: 2;
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
  min-width: 0;
}

/* Dice para que son los controles que siguen. Sin esto, cuatro cajas que
   dicen "All ..." parecen campos para llenar y no filtros. */
.barra-filtros__et {
  flex: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #94A3B8;
  padding-right: 2px;
}

/* Que ninguno de los cuatro se estire mas que los otros, y que puedan
   encogerse: en un solo renglon el ancho es el que sobra, no el que piden. */
.barra-filtros .ms {
  flex: 1 1 140px;
  max-width: 210px;
  min-width: 0;
}

/* Sin ancho para los seis controles seguidos, los filtros vuelven a su
   propia fila en vez de apretarse hasta no leerse. */
@media (max-width: 1280px) {
  .barra-buscar {
    flex: 1 1 320px;
  }

  .barra-filtros {
    order: 3;
    flex: 1 1 100%;
    flex-wrap: wrap;
  }

  .top-bar-right {
    order: 2;
  }
}

.search-input {
  padding: 10px 16px;
  width: 260px;
  border-radius: 8px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E293B;
  font-size: 14px;
}

.search-input:focus {
  outline: none;
  border-color: #3B82F6;
}

.btn-create {
  padding: 10px 18px;
  background: #2F4779;
  color: white;
  border: 1px solid #2F4779;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.btn-create:hover {
  background: #6D7DFF;
  border-color: #6D7DFF;
}

/* TABLE */
.table-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);

  /* Se queda con el alto que sobra despues de cabecera y paginacion.
     min-height: 0 es lo que le permite encogerse y scrollear por dentro:
     sin eso un hijo flex nunca baja del alto de su contenido. */
  flex: 0 1 auto;
  min-height: 0;
  overflow: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table th {
  background: #F1F5F9;
  padding: 14px;
  font-size: 13px;
  color: #475569;
  text-align: left;
  border-bottom: 1px solid #E2E8F0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modern-table td {
  padding: 14px;
  font-size: 14px;
  color: #334155;
  border-bottom: 1px solid #F1F5F9;
}

.modern-table tr:hover td {
  background: #F8FAFC;
}

/* STATUS */
.status {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.status.active {
  background: #DCFCE7;
  color: #15803D;
}

.status.inactive {
  background: #FEE2E2;
  color: #B91C1C;
}

/* ACTIONS */
.action-icon {
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: .2s;
}

.action-icon.view:hover {
  background: #F1F5F9;
}

.action-icon.edit:hover {
  background: #DBEAFE;
}

/* EMPTY */
.no-data {
  text-align: center;
  padding: 20px;
  color: #94A3B8;
}

/* PAGINATION */
/* Tres zonas: el tamano de pagina a la izquierda, Prev/Next al centro y el
   total a la derecha. Los laterales tienen el mismo flex-basis para que el
   centro quede centrado de verdad y no corrido hacia el lado que pesa mas. */
.pagination {
  margin-top: 14px;
  margin-bottom: 2px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
}

/* Los dos lados pesan igual para que el centro quede centrado. */
.pag-lado,
.pag-total {
  flex: 1 1 0;
  min-width: 0;
  font-size: 13px;
  color: #94A3B8;
}

.pag-tamano {
  display: flex;
  align-items: center;
  gap: 7px;

  /* Un respiro extra despues de Next: sin el, "Rows" se lee como si fuera
     parte del boton. */
  margin-left: 10px;
  padding-left: 16px;
  border-left: 1px solid #E2E8F0;
}

.pag-tamano label {
  font-size: 12px;
  color: #94A3B8;
}

.pag-nav {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
}

.pag-total {
  text-align: right;
}

/* Mas chico aca abajo que cuando estaba entre los filtros: no es un control
   que se use a cada rato. */
.page-size-select {
  padding: 6px 8px;
  border-radius: 7px;
  border: 1px solid #E2E8F0;
  background: #F8FAFC;
  font-family: inherit;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
}

.page-size-select:focus {
  outline: none;
  border-color: #3B82F6;
  background: #ffffff;
}

@media (max-width: 900px) {
  /* En pantallas angostas el total sobra antes que los botones. */
  .pag-total,
  .pag-lado { display: none; }

  .pagination { justify-content: center; }
}

.pagination button {
  padding: 8px 14px;
  background: #2F4779;
  color: white;
  border: 1px solid #2F4779;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.pagination button:hover:not(:disabled) {
  background: #6D7DFF;
  border-color: #6D7DFF;
}

.pagination button:disabled {
  opacity: .5;
}

.pagination span {
  font-size: 14px;
  color: #475569;
}

/* BACKDROP */
.adc-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

/* MODAL */
.adc-modal {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.2);
}

.adc-modal-title {
  font-size: 18px;
  font-weight: 800;
  margin-bottom: 4px;
}

.adc-modal-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 16px;
}

.adc-modal-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.adc-modal-form label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.adc-modal-form input,
.adc-modal-form textarea {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  font-size: 14px;
}

.adc-modal-form textarea {
  resize: vertical;
  min-height: 70px;
}

.adc-form-error {
  font-size: 13px;
  color: #b91c1c;
  background: #fee2e2;
  padding: 8px 10px;
  border-radius: 8px;
}

/* ACTIONS */
.adc-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}

.btn-primary {
  padding: 10px 16px;
  background: #3b82f6;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}

.btn-secondary {
  padding: 10px 16px;
  background: #f1f5f9;
  color: #334155;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

.btn-warning {
  padding: 10px 16px;
  background: #f97316; /* naranja */
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
}

.btn-warning:hover {
  background: #ea580c;
}


/* ---------- Celda de producto: nombre + numero de parte ---------- */
.td-producto {
  min-width: 260px;
}

.prod-nombre {
  display: block;
  color: #1E293B;
  font-weight: 600;
  line-height: 1.35;
}

/* El numero de parte y los atributos van debajo, mas chicos y en gris: son
   datos de apoyo, no compiten con el nombre. */
.prod-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 6px 8px;
  margin-top: 6px;
}

/* Cada dato: el valor en una pastilla y debajo que cosa es. */
/* align-items: center centra la etiqueta debajo de su propia pastilla.
   El grupo entero sigue alineado a la izquierda con el nombre: lo unico que
   se centra es cada etiqueta respecto del valor que describe. */
.prod-dato {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
}

.prod-dato-valor {
  display: inline-block;
  max-width: 220px;
  padding: 2px 9px;
  border-radius: 20px;
  background: #F1F5F9;

  font-family: "Consolas", "Courier New", monospace;
  font-size: 11.5px;
  letter-spacing: 0.01em;
  color: #475569;
  line-height: 1.6;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* La etiqueta va debajo y en chico: se lee cuando hace falta, no compite
   con el valor. */
.prod-dato-et {
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #A9B4C4;
  line-height: 1.3;
  white-space: nowrap;
}

/* Activo pero sin publicar. Gris y no rojo a proposito: no es un problema,
   es un producto que todavia no se quiso mostrar. El gris es el mismo que
   ya usa la tabla para los datos de apoyo. */
.status.neutral {
  background: #F1F5F9;
  color: #64748B;
}

/* ---------- Numeros a la derecha ---------- */
/* Alineados a la derecha para que las unidades, decenas y centenas queden
   una debajo de otra y se puedan comparar de un vistazo. */
.th-num,
.td-num {
  text-align: right;
  white-space: nowrap;
}

.td-num {
  font-variant-numeric: tabular-nums;
}

/* El ID es de apoyo: no necesita el mismo peso que el resto. */
.th-id,
.td-id {
  width: 54px;
  color: #94A3B8;
  font-size: 13px;
}

/* La columna de estado lleva la pastilla y debajo el texto de publicacion.
   Sin ancho propio, "Not published" partia en dos lineas y esa fila quedaba
   mas alta que las demas. */
.th-estado,
.td-estado {
  width: 118px;
  white-space: nowrap;
}
`;

